// views/Profile/components/TierCatalogGrid.jsx
import React from 'react';
import TierCatalogCard from './TierCatalogCard';
import './TierCatalogGrid.css';

/**
 * TierCatalogGrid — wrapper for TierCatalogCards.
 *
 * Renders all active tiers (including ENTERPRISE) as equal cards in a
 * single 3-up grid, matching the mockup — there is no special banner
 * treatment for Enterprise.
 *
 * Tiers are ordered by `sortOrder` (then displayName as tiebreaker) so
 * the catalog reads FREE → DEVELOPER → STARTER → PRO → ... regardless
 * of how the backend sequenced them. Inactive tiers are skipped.
 *
 * Props:
 *   tiers:           TierItem[]
 *   currentTierId:   uuid | null  — drives the isCurrent flag.
 *   billingCycle:    'monthly' | 'yearly' — passed through to every card.
 *   checkoutTierId:  uuid | null — the tier currently starting a
 *                    checkout (its CTA shows a spinner + disables while
 *                    this matches its id).
 *   onSwitch:        (tier) => void
 *   onContact:       (tier) => void
 *   onManage:        () => void — passed through to every card; only the
 *                    current tier's card actually renders a CTA that
 *                    calls it.
 */
export default function TierCatalogGrid({
  tiers = [],
  currentTierId,
  billingCycle,
  checkoutTierId,
  canManageEnterprise = false,
  onSwitch,
  onContact,
  onManage,
  onSubscribe,
}) {
  const sortedTiers = [...tiers]
    .filter((t) => t && t.isActive !== false)
    .sort((a, b) => {
      const ao = a.sortOrder ?? 0;
      const bo = b.sortOrder ?? 0;
      if (ao !== bo) return ao - bo;
      return (a.displayName ?? '').localeCompare(b.displayName ?? '');
    });

  if (sortedTiers.length === 0) {
    return null;
  }

  return (
    <div className="tier-grid">
      {sortedTiers.map((tier) => (
        <TierCatalogCard
          key={tier.id}
          tier={tier}
          isCurrent={Boolean(currentTierId && tier.id === currentTierId)}
          billingCycle={billingCycle}
          checkingOut={Boolean(checkoutTierId && tier.id === checkoutTierId)}
          canManageEnterprise={canManageEnterprise}
          onSwitch={() => onSwitch?.(tier)}
          onContact={() => onContact?.(tier)}
          onManage={onManage}
          onSubscribe={() => onSubscribe?.(tier)}
        />
      ))}
    </div>
  );
}
